import React, { useState } from 'react'

import { getErrorMessage, getPersonLabel } from 'utils'

import type { Owner } from 'store/owners/types'

import { useArchiveOwner } from 'hooks/api'

import { Stack, Typography, Avatar, useTheme } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'

interface ArchiveOwnerModalProps {
  owner: Owner | null
  open: boolean
  onClose: () => void
  refetch: () => void
  /** When true the modal performs an unarchive instead of an archive */
  unarchiving?: boolean
}

export const ArchiveOwnerModal = (props: ArchiveOwnerModalProps) => {
  const { owner, open, onClose, refetch, unarchiving = false } = props
  // VERA-304: name if there is one, else the email as the primary line.
  const ownerLabel = getPersonLabel(owner)
  const theme = useTheme()

  const { archiveOwner, unarchiveOwner, isLoading } = useArchiveOwner()

  const [error, setError] = useState<string | null>(null)

  const handleAction = async () => {
    if (!owner) return
    setError(null)

    try {
      const result = unarchiving
        ? await unarchiveOwner(owner.id)
        : await archiveOwner(owner.id)

      if (result.success) {
        handleClose()
        refetch()
      } else {
        setError(getErrorMessage(result.error || `Failed to ${unarchiving ? 'unarchive' : 'archive'} owner`))
      }
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  const title = unarchiving ? 'Unarchive owner' : 'Archive owner'
  const description = unarchiving
    ? 'Restore this owner to the default list?'
    : 'Archive this owner? They will be hidden from the default list. You can reveal archived owners with the "Show Archived" toggle and unarchive at any time.'

  return (
    <Modal
      title={title}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            onClick={handleAction}
            disabled={isLoading}
          >
            {isLoading
              ? unarchiving ? 'Unarchiving...' : 'Archiving...'
              : unarchiving ? 'Unarchive' : 'Archive'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        {error && <Alert severity="error" description={error} />}

        {owner && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 2,
              backgroundColor: theme.palette.common.white,
            }}
          >
            <Avatar
              src={owner.avatar || undefined}
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.primary.main,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {ownerLabel.primary?.charAt(0).toUpperCase() || '?'}
            </Avatar>
            <Stack spacing={0}>
              <Typography variant="body2" fontWeight="500" color="text.primary">
                {ownerLabel.primary || '—'}
              </Typography>
              {/* VERA-304: suppressed when the address IS the primary line above,
                  so a nameless owner is not shown their own email twice. */}
              {ownerLabel.secondary && (
                <Typography variant="caption" color="text.secondary">
                  {ownerLabel.secondary}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
